import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Badge from '@mui/material/Badge'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import MapIcon from '@mui/icons-material/Map'
import { getDiveById } from '../services/diveService'
import { getDiveSiteByName } from '../services/diveSiteService'
import {
  getApprovedCount,
  getUserRequestForDive
} from '../services/joinRequestService'
import DiveStatusControls from '../components/DiveStatusControls'
import DiveParticipants from '../components/DiveParticipants/DiveParticipants'
import JoinRequestButton from '../components/JoinRequestButton'
import CancelBookingButton from '../components/CancelBookingButton'
import DisputeForm from '../components/DisputeForm'
import { InviteForm } from '../components/InviteForm'
import RatingPrompt from '../components/RatingPrompt'
import { useSessionStore } from '../state'
import Database from '../types/Database'

const DiveDetail = () => {
  const { id } = useParams<{ id: string }>()
  const user = useSessionStore(s => s.user)
  const [dive, setDive] = useState<Database.Dive | null>(null)
  const [approvedCount, setApprovedCount] = useState<number>(0)
  const [userJoinRequest, setUserJoinRequest] =
    useState<Database.JoinRequest | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [disputeOpen, setDisputeOpen] = useState(false)
  const [siteImageUrl, setSiteImageUrl] = useState<string | null>(null)

  useEffect(() => {
    const fetchDive = async () => {
      if (!id) {
        setError('Dive ID is missing')
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const result = await getDiveById(id)
        if (!result) {
          setError('Dive not found')
        } else {
          setDive(result)
          getDiveSiteByName(result.siteName)
            .then(site => setSiteImageUrl(site?.pictureUrl ?? null))
            .catch(() => {})
          getApprovedCount(id)
            .then(setApprovedCount)
            .catch(() => {})
          if (user) {
            getUserRequestForDive(id)
              .then(setUserJoinRequest)
              .catch(() => {})
          }
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load dive'
        setError(message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDive()
  }, [id, user])

  const handleStatusChange = (newStatus: string) => {
    if (dive) {
      setDive({ ...dive, status: newStatus as Database.Dive['status'] })
    }
  }

  const handleCancellationSuccess = () => {
    if (!id) return
    getDiveById(id)
      .then(result => {
        if (result) {
          setDive(result)
          getApprovedCount(id)
            .then(setApprovedCount)
            .catch(() => {})
        }
      })
      .catch(() => {})
    getUserRequestForDive(id)
      .then(setUserJoinRequest)
      .catch(() => {})
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error || !dive) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity='error'>{error || 'Dive not found'}</Alert>
      </Box>
    )
  }

  const isPast = dive.dateTime.toDate() < new Date()
  const displayStatus = dive.status

  const formattedDateTime = dive.dateTime.toDate().toLocaleString(undefined, {
    dateStyle: 'long',
    timeStyle: 'short'
  })

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
        >
          <Typography variant='h5' component='h1'>
            {dive.siteName}
          </Typography>
          <Stack direction='row' spacing={1}>
            <Chip
              label={dive.type}
              size='small'
              color='primary'
              variant='outlined'
            />
            <Chip
              label={displayStatus}
              size='small'
              color={
                displayStatus === 'open'
                  ? 'success'
                  : displayStatus === 'full'
                  ? 'warning'
                  : displayStatus === 'cancelled'
                  ? 'error'
                  : 'default'
              }
            />
          </Stack>
        </Box>

        {siteImageUrl && (
          <Box
            component='img'
            src={siteImageUrl}
            alt={dive.siteName}
            sx={{
              width: '100%',
              maxHeight: 300,
              objectFit: 'cover',
              borderRadius: 1
            }}
          />
        )}

        <Typography variant='body1' color='text.secondary'>
          {dive.description}
        </Typography>

        <Divider />

        <Stack spacing={1}>
          <Typography variant='body2'>
            <strong>Date & Time:</strong> {formattedDateTime}
          </Typography>
          <Typography variant='body2'>
            <strong>Duration:</strong> {dive.duration} minutes
          </Typography>
          <Typography variant='body2'>
            <strong>Depth Tier:</strong> {dive.depthTier}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant='body2'>
              <strong>Location:</strong> {dive.location.latitude.toFixed(4)},{' '}
              {dive.location.longitude.toFixed(4)}
            </Typography>
            <IconButton
              size='small'
              color='primary'
              aria-label='Open in maps'
              href={`geo:${dive.location.latitude},${dive.location.longitude}?q=${dive.location.latitude},${dive.location.longitude}`}
            >
              <MapIcon fontSize='small' />
            </IconButton>
          </Box>
          <Typography variant='body2'>
            <strong>Meeting Point:</strong> {dive.meetingPoint}
          </Typography>
          <Typography variant='body2'>
            <strong>Spots Filled:</strong> {approvedCount} /{' '}
            {dive.maxParticipants}
          </Typography>
          {dive.type === 'guided' && dive.price != null && (
            <Typography variant='body2'>
              <strong>Price:</strong> ${dive.price}
            </Typography>
          )}
          <Typography variant='body2'>
            <strong>Pending Requests:</strong> {dive.pendingRequestCount ?? 0}
          </Typography>
          {dive.notes && (
            <Typography variant='body2'>
              <strong>Notes:</strong> {dive.notes}
            </Typography>
          )}
        </Stack>

        <Divider />

        <DiveParticipants diveId={dive.id} hostId={dive.hostId} />

        <DiveStatusControls
          diveId={dive.id}
          hostId={dive.hostId}
          status={displayStatus as 'open' | 'full' | 'cancelled' | 'completed'}
          isPast={isPast}
          onStatusChange={handleStatusChange}
        />

        {user && user.uid === dive.hostId && (
          <Badge
            badgeContent={dive.pendingRequestCount ?? 0}
            color='error'
            max={99}
          >
            <Button
              component={Link}
              to={`/dives/${dive.id}/requests`}
              variant='outlined'
            >
              View Requests
            </Button>
          </Badge>
        )}

        {user && user.uid === dive.hostId && (
          <InviteForm
            diveId={dive.id}
            hostUid={dive.hostId}
            diveStatus={displayStatus}
          />
        )}

        {user && user.uid !== dive.hostId && (
          <JoinRequestButton
            diveId={dive.id}
            diveStatus={displayStatus}
            hostId={dive.hostId}
            diveType={dive.type}
          />
        )}

        {user && userJoinRequest && (
          <CancelBookingButton
            joinRequestId={userJoinRequest.id}
            joinRequestStatus={userJoinRequest.status}
            paymentStatus={userJoinRequest.paymentStatus}
            requesterId={userJoinRequest.requesterId}
            diveId={dive.id}
            diveStatus={displayStatus}
            diveDateTime={dive.dateTime.toDate()}
            onSuccess={handleCancellationSuccess}
          />
        )}

        {user && dive.status === 'completed' && (
          <RatingPrompt diveId={dive.id} currentUserId={user.uid} />
        )}

        {user &&
          userJoinRequest &&
          userJoinRequest.paymentStatus === 'paid' &&
          (() => {
            const diveDate = dive.dateTime.toDate()
            const now = new Date()
            const hoursUntilDive =
              (diveDate.getTime() - now.getTime()) / (1000 * 60 * 60)
            const cannotCancel =
              displayStatus === 'completed' ||
              displayStatus === 'cancelled' ||
              hoursUntilDive <= 48
            return cannotCancel
          })() && (
            <>
              <Button
                variant='outlined'
                color='warning'
                onClick={() => setDisputeOpen(true)}
              >
                Raise Dispute
              </Button>
              <DisputeForm
                open={disputeOpen}
                onClose={() => setDisputeOpen(false)}
                diveId={dive.id}
              />
            </>
          )}
      </Stack>
    </Box>
  )
}

export default DiveDetail
